import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import imgRank1 from "./assets/rank-1.svg";
import imgRank2 from "./assets/rank-2.svg";
import imgRank3 from "./assets/rank-3.svg";
import imgLogo from "./assets/logo.png";
import { ContainerShape, InnerShape } from "./components/Shape";
import { ProfilePicture } from "./components/ProfilePicture";
import Loader from "~/core/components/Loader";
import { formatNumber } from "~/core/utils/formatNumber";
import { api, type BlinkoSinceResetLeaderboardEntry } from "./api";

const CODE_RE = /^[A-Za-z0-9_-]{2,32}$/;

type LocalEntry = BlinkoSinceResetLeaderboardEntry & { localRank: number };

const formatHoney = (honey: string) => {
  const num = parseInt(honey, 10);
  if (!Number.isFinite(num)) return "0";
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return formatNumber(num);
};

const formatSeasonStart = (seconds: number) =>
  new Date(seconds * 1000).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const stripAt = (handle: string) => (handle.startsWith("@") ? handle.slice(1) : handle);

const truncate = (addr: string) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;

const PODIUM_THEME: Record<1 | 2 | 3, { ring: string; bg: string; label: string; rankImg: string }> = {
  1: {
    ring: "ring-2 ring-[#FFD700]",
    bg: "bg-gradient-to-b from-[#FFD70033] to-transparent",
    label: "text-[#FFD700]",
    rankImg: imgRank1,
  },
  2: {
    ring: "ring-2 ring-[#C0C0C0]",
    bg: "bg-gradient-to-b from-[#C0C0C033] to-transparent",
    label: "text-[#D8D8D8]",
    rankImg: imgRank2,
  },
  3: {
    ring: "ring-2 ring-[#CD7F32]",
    bg: "bg-gradient-to-b from-[#CD7F3233] to-transparent",
    label: "text-[#E0934E]",
    rankImg: imgRank3,
  },
};

const PodiumCard = ({ entry }: { entry: LocalEntry }) => {
  const rank = entry.localRank as 1 | 2 | 3;
  const theme = PODIUM_THEME[rank];
  const handle = entry.twitterHandle ? stripAt(entry.twitterHandle) : null;
  const onClick = () => {
    if (handle) window.open(`https://x.com/${handle}`, "_blank");
  };

  return (
    <div
      className={`relative ${theme.bg} rounded-xl p-4 flex flex-col items-center gap-2 ${
        handle ? "cursor-pointer" : ""
      }`}
      onClick={onClick}
    >
      <img src={theme.rankImg} className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7" alt={`Rank ${rank}`} />
      <div className={`w-16 h-16 rounded-full overflow-hidden ${theme.ring} mt-2`}>
        <ProfilePicture src={entry.picture ?? undefined} twitterHandle={entry.twitterHandle} />
      </div>
      <p className={`text-xs uppercase font-blinko tracking-wider ${theme.label}`}>#{rank}</p>
      <p className="text-sm font-bold text-white text-center truncate max-w-full">
        {entry.name || truncate(entry.address)}
      </p>
      {handle && <p className="text-[10px] text-blue-400 truncate max-w-full">@{handle}</p>}
      <div className="rounded mt-1" style={{ border: "1px solid #FFF085" }}>
        <div className="px-2 py-0.5 bg-[#F0B10080]">
          <p className="text-xs font-semibold text-white whitespace-nowrap">
            {formatHoney(entry.harvestedHoney)}
          </p>
        </div>
      </div>
    </div>
  );
};

const ListRow = ({ entry }: { entry: LocalEntry }) => {
  const handle = entry.twitterHandle ? stripAt(entry.twitterHandle) : null;
  const onClick = () => {
    if (handle) window.open(`https://x.com/${handle}`, "_blank");
  };

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
        handle ? "cursor-pointer hover:bg-white/5" : ""
      }`}
      onClick={onClick}
    >
      <div className="w-8 shrink-0">
        <p className="text-sm font-medium text-white/80">#{entry.localRank}</p>
      </div>
      <div className="w-8 h-8 shrink-0 rounded-full overflow-hidden">
        <ProfilePicture src={entry.picture ?? undefined} twitterHandle={entry.twitterHandle} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {entry.name || truncate(entry.address)}
        </p>
        {handle && <p className="text-[10px] text-blue-400 truncate">@{handle}</p>}
      </div>
      <div className="rounded shrink-0" style={{ border: "1px solid #FFF085" }}>
        <div className="px-2 py-0.5 bg-[#F0B10080]">
          <p className="text-xs font-semibold text-white whitespace-nowrap">
            {formatHoney(entry.harvestedHoney)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function ClanPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const validCode = !!code && CODE_RE.test(code);

  const { data, isLoading, error } = useSWR(
    validCode ? ["clan-leaderboard-since-reset", code] : null,
    () => api.getClanLeaderboardSinceReset(code!),
    { refreshInterval: 30_000, revalidateOnFocus: false }
  );

  const entries: LocalEntry[] = useMemo(() => {
    if (!data) return [];
    const sorted = [...data.leaderboard].sort((a, b) => {
      const ah = BigInt(a.harvestedHoney);
      const bh = BigInt(b.harvestedHoney);
      if (ah !== bh) return bh > ah ? 1 : -1;
      return a.rank - b.rank;
    });
    return sorted.map((e, idx) => ({ ...e, localRank: idx + 1 }));
  }, [data]);

  const onJoinAndPlay = () => {
    if (!code) return;
    navigate(`/?r=${encodeURIComponent(code)}`);
  };

  if (!validCode) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-red-400">Invalid clan code</p>
      </div>
    );
  }

  const top3Sorted = entries.slice(0, 3);
  const top3 = [top3Sorted[1], top3Sorted[0], top3Sorted[2]].filter(Boolean) as LocalEntry[];
  const rest = entries.slice(3);

  return (
    <div className="h-full bg-black text-white overflow-hidden flex items-center justify-center p-3 md:p-4 pt-1 md:pt-2">
      <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col items-center gap-3 h-full">
        {/* Logo */}
        <img src={imgLogo} alt="Blinko" className="h-10 md:h-20 w-auto shrink-0" />

        <div className="w-full min-h-0 flex-1 flex flex-col">
          <ContainerShape cornerDiagonal={20} cornerRadius={12} strokeWidth={1}>
            <div className="p-3 md:p-4 backdrop-blur-sm flex flex-col h-full min-h-0">
              {/* Header */}
              <div className="flex items-center gap-4 mb-4 shrink-0">
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <h1 className="text-xl md:text-2xl font-black text-white truncate">Clan Leaderboard</h1>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/40">
                    {data && (
                      <span>
                        {data.leaderboard.length} member{data.leaderboard.length === 1 ? "" : "s"}
                      </span>
                    )}
                    {data?.since != null && (
                      <span>Season started {formatSeasonStart(data.since)}</span>
                    )}
                  </div>
                </div>
                <InnerShape cornerDiagonal={8} cornerRadius={6} strokeWidth={1}>
                  <div className="px-4 py-2 text-center">
                    <p className="text-[10px] text-white/50 uppercase tracking-wider">Code</p>
                    <p className="text-lg md:text-xl font-black text-yellow-400">{code}</p>
                  </div>
                </InnerShape>
              </div>

              {/* Body */}
              {isLoading && !data ? (
                <div className="flex justify-center items-center flex-1">
                  <Loader size="48px" stroke="#ffffff" />
                </div>
              ) : error ? (
                <div className="flex justify-center items-center flex-1">
                  <p className="text-red-400">Failed to load clan leaderboard.</p>
                </div>
              ) : entries.length === 0 ? (
                <div className="flex justify-center items-center flex-1">
                  <p className="text-white/50">No members yet.</p>
                </div>
              ) : (
                <div className="flex flex-col min-h-0 flex-1">
                  {/* Top 3 podium */}
                  {top3.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 items-end shrink-0">
                      {top3.map((e) => (
                        <PodiumCard key={e.address} entry={e} />
                      ))}
                    </div>
                  )}

                  {/* Rest of leaderboard */}
                  {rest.length > 0 && (
                    <div className="min-h-0 flex-1 rounded-xl border border-[#FFE10C50] bg-[#F0B10026] overflow-hidden mb-3">
                      <div className="h-full overflow-y-auto p-2 md:p-3">
                        <div className="flex flex-col gap-1">
                          {rest.map((e) => (
                            <ListRow key={e.address} entry={e} />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Join & Play */}
                  <button
                    className="w-full shrink-0 rounded-lg bg-yellow-400 hover:bg-yellow-300 transition-colors active:scale-[0.98] px-8 py-3 md:py-4"
                    onClick={onJoinAndPlay}
                  >
                    <p className="text-base md:text-lg font-blinko text-black text-center font-black">
                      Join & Play
                    </p>
                  </button>
                </div>
              )}
            </div>
          </ContainerShape>
        </div>
      </div>
    </div>
  );
}
